import React from "react";
import { Button, Checkbox, CheckboxProps, Col, DatePicker, Flex, Form, Input, Row, Switch, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DATE_FORMAT } from "../../../constants/common";
import { renderClassNameStatusEsp, roundCost } from "../../../utils/helper";
import FormattedNumberInput from "../../../components/format-number-input";
import { IEspTable } from "../../../interfaces/expect-supplier-payment";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { PERMISSIONS } from "../../../constants/permission";
import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { EspStatusEnum } from "../../../constants/enum";
interface MatchingEspFormProps {
  form: any;
  espId: string;
  isDisabledByStatus: boolean;
  isTypeFlight: boolean;
  onHandleChangePg?: (isCheck: boolean) => void;
  isViewPg?: boolean;
}

const MatchingEspForm: React.FC<MatchingEspFormProps> = ({
  form,
  espId,
  isDisabledByStatus = true,
  isTypeFlight,
  onHandleChangePg,
  isViewPg = true,
}) => {
  const expectedSupplierPayments = Form.useWatch("expectedSupplierPayments", form) || [];
  const hasEditor = useHasPermission([PERMISSIONS.BOOKING_SUPPLIER_COST_EDITOR]);
  const isDisabledField = !hasEditor || !isDisabledByStatus;

  const handleChangePg: CheckboxProps["onChange"] = (e) => {
    onHandleChangePg?.(e.target.checked);
  };

  const isCheckDisabledRow = (row: IEspTable) => {
    const { isActive, status } = row || {};
    if (!isActive || status === EspStatusEnum.EXPORTED) {
      return true;
    }
    return false;
  };

  const isDisabledByEspStatus = (row: IEspTable) => row?.status === EspStatusEnum.EXPORTED;

  return (
    <Form form={form} layout="horizontal" disabled={isDisabledField}>
      <Flex justify="space-between" align="center">
        <h4 className="sub-title">Booking Supplier Cost:</h4>
        {isViewPg && (
          <Form.Item label="" name="pgBookings" valuePropName="checked" style={{ marginBottom: "10px" }}>
            <Checkbox onChange={handleChangePg}>PG Bookings</Checkbox>
          </Form.Item>
        )}
      </Flex>

      <Row gutter={24} className="fm-view-page">
        <Col span={12}>
          <Form.Item label="Supplier Code" name="supplierCode" labelCol={{ span: 11 }}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Supplier Name" name="supplierName" labelCol={{ span: 11 }}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Booking Reference ID" name="bookingRef" labelCol={{ span: 11 }}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Local Currency Cost" name="localCurrencyCost" labelCol={{ span: 11 }}>
            <FormattedNumberInput className="w-full " disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="GBP Cost" name="gbpCost" labelCol={{ span: 11 }}>
            <FormattedNumberInput className="w-full " disabled />
          </Form.Item>
        </Col>
      </Row>
      {!isDisabledField && (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const bscCost = roundCost(getFieldValue("localCurrencyCost") || 0);
            const espTables = getFieldValue(["expectedSupplierPayments"]) || [];
            const totalEspCost = roundCost(
              espTables
                .filter((r: IEspTable) => r.isActive)
                .reduce((sum: number, r: any) => sum + (r?.localCurrencyCost || 0), 0)
            );

            if (roundCost(totalEspCost) !== roundCost(bscCost)) {
              return (
                <div
                  className="text-error"
                  style={{ textAlign: "center", marginBottom: 16 }}
                >{`Cost mismatched between ESP and BSC, the remaining cost is ${(bscCost - totalEspCost).toFixed(
                  2
                )}`}</div>
              );
            }
            return null;
          }}
        </Form.Item>
      )}

      <Form.List name="expectedSupplierPayments">
        {(fields, { add }) => {
          const columns: ColumnsType<any> = [
            {
              title: "Status",
              dataIndex: "status",
              width: 100,
              render: (_: any, record: any) => (
                <Form.Item shouldUpdate>
                  {({ getFieldValue }) => {
                    const status = getFieldValue(["expectedSupplierPayments", record.name, "status"]);
                    return <span className={renderClassNameStatusEsp(status)}>{status}</span>;
                  }}
                </Form.Item>
              ),
            },
            {
              title: "Supplier Code",
              dataIndex: "supplierCode",
              width: 120,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, "supplierCode"]}>
                  <Input placeholder="Enter Supplier Code" disabled />
                </Form.Item>
              ),
            },
            {
              title: "Booking Reference ID",
              dataIndex: "bookingNo",
              width: 145,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, "bookingNo"]} rules={[{ required: true, message: "" }]}>
                  <Input placeholder="Enter booking ref" disabled />
                </Form.Item>
              ),
            },
            {
              title: isTypeFlight ? "PNR Ref" : "Lead Pax Name",
              dataIndex: isTypeFlight ? "pnrRef" : "leadPax",
              width: 145,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, isTypeFlight ? "pnrRef" : "leadPax"]}>
                  <Input placeholder={`Enter ${isTypeFlight ? "PNR Ref" : "Lead Pax Name"}`} disabled />
                </Form.Item>
              ),
            },
            {
              title: "Due Date",
              dataIndex: "dueDate",
              width: 145,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, "dueDate"]} rules={[{ required: true, message: "" }]}>
                  <DatePicker
                    className="w-full input-t-r"
                    format={DATE_FORMAT}
                    placeholder="Select due date"
                    disabled={isDisabledField || isCheckDisabledRow(expectedSupplierPayments?.[record.name])}
                  />
                </Form.Item>
              ),
            },
            {
              title: "Local Currency Cost",
              dataIndex: "localCurrencyCost",
              width: 125,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, "localCurrencyCost"]} rules={[{ required: true, message: "" }]}>
                  <FormattedNumberInput
                    className="w-full input-t-r"
                    placeholder="Insert cost data here"
                    disabled={isDisabledField || isCheckDisabledRow(expectedSupplierPayments?.[record.name])}
                  />
                </Form.Item>
              ),
            },
            {
              title: "GBP Cost",
              dataIndex: "gbpCost",
              width: 125,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, "gbpCost"]}>
                  <FormattedNumberInput
                    className="w-full input-t-r"
                    disabled={true}
                  />
                </Form.Item>
              ),
            },
            {
              title: "Active",
              dataIndex: "isActive",
              width: 100,
              align: "center" as const,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, "isActive"]} valuePropName="checked" style={{ display: "inline-block" }}>
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    disabled={
                      isDisabledField ||
                      expectedSupplierPayments?.[record.name]?.id === espId ||
                      isDisabledByEspStatus(expectedSupplierPayments?.[record.name])
                    }
                  />
                </Form.Item>
              ),
            },
          ];
          const activeFields = fields.filter((f) => {
            const item = form.getFieldValue(["expectedSupplierPayments", f.name]);
            return item?.isActive;
          });
          return (
            <>
              <h4 className="sub-title">Expected Supplier Payment Records</h4>
              <Table
                dataSource={activeFields}
                pagination={false}
                rowKey={(f) => f.key}
                columns={columns}
                size="small"
                className="table-fm table-esp"
                scroll={{ y: 50 * 6 }}
                rowClassName={(record) => {
                  const item = form.getFieldValue(["expectedSupplierPayments", record.name]);
                  if (item.id === espId) return "active-row";

                  return "";
                }}
              />
              {!isDisabledField && (
                <Button
                  color="primary"
                  variant="outlined"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const esp = expectedSupplierPayments.find((item: any) => item.id === espId);
                    add({
                      ...esp,
                      id: "",
                      status: "Unmatched",
                      dueDate: undefined,
                      localCurrencyCost: undefined,
                      isActive: true,
                    });
                    setTimeout(() => {
                      const tableBody = document.querySelector(".table-esp .ant-table-body") as HTMLElement;

                      if (tableBody) {
                        tableBody.scrollTo({
                          top: tableBody.scrollHeight,
                          behavior: "smooth",
                        });
                      }
                    }, 100);
                  }}
                  style={{ marginTop: "16px" }}
                >
                  Add row
                </Button>
              )}
            </>
          );
        }}
      </Form.List>
    </Form>
  );
};

export default MatchingEspForm;
